/**
 * workAreasStore.ts
 *
 * Raw read/write for the `workAreas` object store.
 *
 * Mirrors every method from workAreaContext:
 *   workAreas          → getAllWorkAreas()
 *   refreshWorkAreas   → bulkLoadWorkAreas()
 *   createWorkArea     → addWorkArea()     (called by workAreaMutations)
 *   updateWorkArea     → updateWorkArea()  (called by workAreaMutations)
 *   deleteWorkArea     → removeWorkArea()  (called by workAreaMutations)
 */

import { appDB } from "../db/client";
import type { WorkAreaRecord } from "../db/schema";
import type { WorkArea } from "@/types/workarea";

// ─── Converters ───────────────────────────────────────────────────

/** Convert a WorkArea (from API) into a WorkAreaRecord (for IDB). */
export function toWorkAreaRecord(
  workArea: WorkArea,
  isSync: 0 | 1 = 1
): WorkAreaRecord {
  return {
    ...workArea,
    isSync,
    localUpdatedAt: Date.now(),
    syncAttempts: 0,
    syncError: null,
  };
}

/** Convert a WorkAreaRecord back to a plain WorkArea (for existing components). */
export function toWorkArea(record: WorkAreaRecord): WorkArea {
  const { isSync, localUpdatedAt, syncAttempts, syncError, ...workArea } = record;
  return workArea as WorkArea;
}

// ─── Reads ────────────────────────────────────────────────────────

/** Get all work areas for a workspace. */
export async function getAllWorkAreas(workspaceId: string): Promise<WorkAreaRecord[]> {
  return appDB.workAreas
    .where("workspaceId")
    .equals(workspaceId)
    .toArray();
}

/** Get a single work area by ID. */
export async function getWorkArea(workAreaId: string): Promise<WorkAreaRecord | undefined> {
  return appDB.workAreas.get(workAreaId);
}

// ─── Writes ───────────────────────────────────────────────────────

/**
 * Bulk load work areas from API response.
 * Used during initial hydration. Marks all as isSync: 1.
 */
export async function bulkLoadWorkAreas(workAreas: WorkArea[]): Promise<void> {
  const records = workAreas.map((wa) => toWorkAreaRecord(wa, 1));
  await appDB.workAreas.bulkPut(records);
}

/**
 * Add a new work area locally.
 * isSync: 0 — watcher will create it in MongoDB.
 */
export async function addWorkArea(workArea: WorkArea): Promise<void> {
  await appDB.workAreas.put(toWorkAreaRecord(workArea, 0));
}

/**
 * Update specific fields on an existing work area.
 * Marks isSync: 0 | 1 = 0 so watcher picks it up.
 */
export async function updateWorkArea(
  workAreaId: string,
  updates: Partial<WorkArea>
): Promise<void> {
  const existing = await appDB.workAreas.get(workAreaId);
  if (!existing) return;
  await appDB.workAreas.put({
    ...existing,
    ...updates,
    _id: workAreaId,
    isSync: 0 as 0,
    localUpdatedAt: Date.now(),
  });
}

/**
 * Remove a work area from IDB.
 * Hard delete is safe here since work areas are not content blocks.
 */
export async function removeWorkArea(workAreaId: string): Promise<void> {
  await appDB.workAreas.delete(workAreaId);
}

/**
 * Mark a work area as synced after watcher confirms server write.
 */
export async function markWorkAreaSynced(workAreaId: string): Promise<void> {
  await appDB.workAreas.update(workAreaId, {
    isSync: 1 as 1,
    syncAttempts: 0,
    syncError: null,
  });
}

/**
 * Record a sync failure on a work area.
 */
export async function recordWorkAreaSyncFailure(
  workAreaId: string,
  error: string
): Promise<void> {
  const existing = await appDB.workAreas.get(workAreaId);
  if (!existing) return;
  await appDB.workAreas.update(workAreaId, {
    syncAttempts: (existing.syncAttempts ?? 0) + 1,
    syncError: error,
  });
}

// ─── Unsynced query (used by watcher) ────────────────────────────

/** Get all work areas with pending local changes. */
export async function getUnsyncedWorkAreas(workspaceId: string): Promise<WorkAreaRecord[]> {
  return appDB.workAreas
    .where("workspaceId")
    .equals(workspaceId)
    .and((wa) => wa.isSync === 0)
    .toArray();
}
